import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape DSP-05 publishes, imported rather than restated. A column here
// that the service stopped returning is a build failure, not a blank cell.
import type { DispatchOperation } from "@tdelivr/api-client/generated/dispatch/dispatch-operations-read-model.js";

import {
  dispatchDetail,
  queuePage,
  readableStatus,
  rowTint,
  type QueueOrder,
} from "../demand-queue.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { createManualAssignClient } from "../manual-assign.ts";
import { ManualAssign } from "./ManualAssign.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * The demand queue: dispatches waiting for a driver.
 *
 * Built to `19 WP18 Dispatch Operations.dc.html`, page `queue`, with every measurement read from the
 * rendered design rather than from its source: the page body is a 16px-gap grid padded
 * `20px 22px 28px`; the tile grid is four `minmax(0, 1fr)` tracks 12px apart, each tile 14px radius
 * with `14px 16px` of padding, a 12.5px label, a 26px count at `-0.02em` and a pill; the data grid is
 * a 16px-radius card holding a `12px 14px` toolbar, a `10px 16px` header strip on `--sunken`,
 * `12px 16px` rows and a `10px 14px` pager; the grid's tracks are
 * `minmax(0, 1.5fr) … 190px` with a 14px gap; and the closing note is a 14px-radius `--t-sky`
 * callout with 20px of glyph and 13.5/20 text.
 *
 * **What is here is what DSP-05 publishes, and that is less than the design draws.** The design's
 * `Pickup` and `Waiting` columns and three of its four tiles have no source in any contract — not in
 * DSP-05, and not in anything DSP-05 could be joined to. Those are left out rather than filled in,
 * and the evidence is in R-08 of `docs/design/DESIGN_RECONCILIATION.md`. A column of plausible
 * numbers is worse than a column that is not there: an operator would work it.
 *
 * The grid's own tracks follow from that. The design derives them from its column list —
 * `minmax(0, 1.5fr)` for the first, `minmax(0, 1fr)` for each of the rest and a fixed 190px for the
 * state cell — so a queue with one sourced column is `minmax(0, 1.5fr) 190px` by the design's own
 * rule rather than by a new one.
 *
 * **The toolbar works over what the screen holds.** DSP-05 publishes no search, no sort and no total
 * (R-01, R-04), so the designed toolbar is only buildable over a bounded set — and a live dispatch
 * queue is bounded by its nature. When the service says there is more than was fetched, the screen
 * says so rather than letting a partial queue read as whole.
 */

const dispatch = services.dispatch__dispatch_operations_read_model;

/**
 * How many rows to take.
 *
 * 200 is DSP-05's own maximum `limit`. A tenant with more than 200 dispatches live at once has a
 * problem this screen cannot help with, and the honest thing then is to say the queue is partial.
 */
const PER_FETCH = "200";

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: QueueOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

export interface DemandQueueProps {
  readonly transport: Transport;
  readonly tenantId: string;
  /** Innermost last: tenant, section, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /** Injected so the stale threshold can be exercised without waiting six seconds. */
  readonly now?: () => number;
}

interface Loaded {
  readonly rows: readonly DispatchOperation[];
  /** DSP-05 answered a cursor, so this is the head of the queue rather than all of it. */
  readonly truncated: boolean;
}

export function DemandQueue({ transport, tenantId, now, crumbs }: DemandQueueProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [observedAt, setObservedAt] = useState<number | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [selectedDemandId, setSelectedDemandId] = useState<string | undefined>(undefined);
  const [assigning, setAssigning] = useState(false);
  /*
   * The manual path's client, built once from the transport this screen already has.
   *
   * `useMemo` rather than a module-level singleton: the transport carries the signed-in
   * operator's own credential, and a client held across sessions is a client holding the
   * previous one's.
   */
  const manual = useMemo(() => createManualAssignClient(transport), [transport]);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<QueueOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    const client = dispatch.createDispatchOperationsReadModelClient(transport);
    try {
      const answered = await client.listDispatchOperations(
        tenantId,
        undefined,
        undefined,
        PER_FETCH,
      );
      setLoaded({
        rows: answered.items,
        truncated: typeof answered.nextCursor === "string" && answered.nextCursor.length > 0,
      });
      // The projection's own watermark, not the moment the browser asked. A
      // screen that timestamped the request would call data fresh when the
      // pipeline behind it had stopped an hour ago.
      setObservedAt(answered.observedAt === null ? undefined : Date.parse(answered.observedAt));
      setFailure(undefined);
    } catch (error) {
      // One state rather than a banner over a half-rendered table. A queue
      // showing four of seven rows with no explanation is worse than one
      // showing none.
      setFailure(stateForError(error, ["dispatch-operations:read"]));
      setLoaded(undefined);
    }
  }, [transport, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The export menu closes on Escape and on a click outside, which the design file never needed to
  // handle because it is a reference rather than something an operator uses for a whole shift.
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const view = useMemo(
    () =>
      queuePage(loaded?.rows ?? [], {
        search,
        ...(status === undefined ? {} : { status }),
        order,
        page,
        perPage,
      }),
    [loaded, search, status, order, page, perPage],
  );

  /*
   * The selected dispatch, found across the whole fetched set rather than the current page.
   *
   * `view.rows` is one page of a filtered, ordered list, so a selection looked up there would
   * vanish the moment an operator turned the page or narrowed the filter — with the panel still
   * open beside it, describing a row no longer on screen. Looking it up in what the board holds
   * keeps the panel and the grid independent, which is what the design's layout implies.
   */
  const selected = loaded?.rows.find((row) => row.demandId === selectedDemandId);

  const state =
    failure ??
    stateForList(loaded?.rows, {
      ...(observedAt === undefined ? {} : { observedAt }),
      ...(now === undefined ? {} : { now: now() }),
    });

  const clearFilters = (): void => {
    setSearch("");
    setStatus(undefined);
    setPage(1);
  };

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="queue__head">
      <span>Request</span>
      <span className="queue__head--right">State</span>
    </div>
  );

  return (
    <section className="page queue">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Demand queue"
        // The design says "DSP-01 · DSP-02". This screen reads DSP-05, which is the only operation on
        // the platform that answers a list of dispatches at all, so the chip names what is actually
        // being read. R-08 records the difference.
        source="DSP-05"
        description="Requests waiting for a driver, with how long they have waited and why."
        action={
          <button
            type="button"
            className="queue__primary"
            /*
             * Live, after a year of being drawn and refused.
             *
             * It was disabled with this note: "No published operation assigns a hand-picked driver
             * to a demand. `POST /v1/ride-assignments` needs an `offerId` for an offer a driver has
             * already accepted, and `POST /v1/ride-offers` needs a `candidateRef` carrying DSP-01's
             * own `generation` and `rank`." Every word was true, and the conclusion — that the
             * button could not be built — was the wrong one: both facts describe work that belongs
             * in DSP-04, which gathers supply, ranks it and forms those references for the automatic
             * dispatch already. R-08 is closed by two operations there rather than by a payload
             * invented here.
             *
             * Disabled only until a row is chosen, because a ranking needs the demand's own pickup,
             * product and version. Q-259.
             */
            disabled={selectedDemandId === undefined}
            title={
              selectedDemandId === undefined
                ? "Choose a request first — ranking drivers needs the demand it is for."
                : undefined
            }
            onClick={() => {
              setAssigning(true);
            }}
          >
            <span aria-hidden="true" className="queue__primary-glyph">
              add
            </span>
            Manual assign
          </button>
        }
      />

      <div className="queue__tiles">
        {/*
          One tile, where the design has four.

          "Waiting" is a count of the live dispatches this screen holds, which DSP-05 can answer
          because its list is live by default. "Over 5 min" needs how long each request has waited,
          "No supply nearby" needs a classified failure reason, and "Manual assign" needs a request
          nothing records. None of the three has a source — R-08 — so they are absent rather than
          estimated.
        */}
        <div className="queue__tile">
          <span className="queue__tile-label">Waiting</span>
          <span className="queue__tile-count">{loaded?.rows.length ?? 0}</span>
          <StateBadge code="DSP-05" status="live" classKey="active" label="now" />
        </div>
      </div>

      {/*
        The grid and its panel in one positioned container, which is what docks the panel.

        **This wrapper was missing, and the panel was therefore docked nowhere.** The stylesheet
        docks `.detail` two ways and both are expressed against `.board-split`: at 1240 and above
        `.board-split:has(> .detail)` makes it a 420px second column, and below 1240 the panel is
        `position: absolute; inset: 0 0 0 auto`, which positions against `.board-split`'s own
        `position: relative`. Those rules were written for the three boards that had a panel then —
        exceptions, broadcasts and departures — and **none of the six boards that gained
        `RecordDetail` afterwards wraps itself in one**, so at 1440 this panel was a full-width
        block stacked under the table, and below 1240 its overlay anchored to the page rather than
        to the board: measured at 1140x552 and 440x1200 respectively, against a design that draws a
        680px card.

        Measured only because `check-demand-queue.mts` gained a `row detail` state on 1 November;
        this screen's panel had never been photographed. The other five boards are named in
        `docs/OPEN_WORK.md` and are deliberately untouched until their own checks can measure the
        change — see `tests/contract/a-detail-panel-is-photographed-or-recorded.test.ts`, which is
        the list of checks that cannot yet. Which docking is right for these six at all is Q-297:
        `19 WP18 Dispatch Operations` draws a centred modal where `04 Dispatch Exceptions Board`
        draws this column, and that is a decision rather than a defect.
      */}
      <div className="board-split">
        <div className="queue__panel">
          <div className="queue__toolbar">
            <span className="queue__search">
              <span aria-hidden="true" className="queue__search-glyph">
                search
              </span>
              <input
                id="demand-queue-search"
                type="search"
                aria-label="Search"
                placeholder="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </span>
            <select
              id="demand-queue-status"
              aria-label="Filter by state"
              value={status ?? ""}
              onChange={(event) => {
                setStatus(event.target.value === "" ? undefined : event.target.value);
                setPage(1);
              }}
            >
              <option value="">All states</option>
              {view.states.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <select
              id="demand-queue-order"
              aria-label="Sort"
              value={order}
              onChange={(event) => {
                setOrder(event.target.value as QueueOrder);
              }}
            >
              {ORDER_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <span className="queue__export" ref={exportRegion}>
              <button
                type="button"
                className="queue__export-button"
                aria-expanded={exportOpen}
                onClick={() => {
                  setExportOpen((open) => !open);
                }}
              >
                <span aria-hidden="true" className="queue__export-glyph">
                  download
                </span>
                Export
              </button>
              {exportOpen ? (
                <div className="queue__export-menu" role="menu">
                  {EXPORTS.map((choice) => (
                    <button
                      key={choice.format}
                      type="button"
                      role="menuitem"
                      className="queue__export-item"
                      onClick={() => {
                        setExportOpen(false);
                        exportRows(view.all, choice.format);
                      }}
                    >
                      <span aria-hidden="true" className="queue__export-item-glyph">
                        {choice.glyph}
                      </span>
                      {choice.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </span>
          </div>

          <StateView
            state={state}
            purpose="Dispatches waiting for a driver appear here as soon as the platform starts looking."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 2,
              rowClassName: "queue__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="queue__rows">
              {view.rows.map((row) => {
                const tint = rowTint(row);
                return (
                  <div
                    key={row.demandId}
                    className={tint === undefined ? "queue__row" : `queue__row queue__row--${tint}`}
                    data-selected={row.demandId === selectedDemandId ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={row.demandId === selectedDemandId}
                    aria-label={`Open dispatch ${row.demandId}`}
                    onClick={() => setSelectedDemandId(row.demandId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedDemandId(row.demandId);
                      }
                    }}
                  >
                    <span className="queue__request">{row.demandId}</span>
                    <span className="queue__state">
                      {/*
                      DSP-04 rather than DSP-05: this screen reads the projection, and the five
                      statuses on it belong to the dispatch saga, which is what owns the lifecycle.
                    */}
                      <StateBadge
                        code="DSP-04"
                        status={row.status}
                        label={readableStatus(row.status)}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {view.total === 0 ? (
              <div className="queue__no-match">
                <span aria-hidden="true" className="queue__no-match-glyph">
                  search_off
                </span>
                Nothing matches this search or filter.
                <button type="button" className="queue__clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}
            <div className="queue__pager">
              <span>
                {view.total === 0
                  ? "No matching rows"
                  : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
              </span>
              <span className="queue__pager-controls">
                <select
                  id="demand-queue-per-page"
                  aria-label="Rows per page"
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice} per page
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <span aria-hidden="true">chevron_left</span>
                </button>
                <span className="queue__page">
                  Page {Math.min(page, view.pageCount)} of {view.pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= view.pageCount}
                  onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
                >
                  <span aria-hidden="true">chevron_right</span>
                </button>
              </span>
            </div>
          </StateView>

          {loaded?.truncated === true ? (
            // Said rather than hidden. The fetch is bounded, and an operator working a partial queue
            // while believing it whole is the failure this line exists to prevent.
            <p className="queue__bounded" role="status">
              More than {PER_FETCH} dispatches are live. This screen shows the first {PER_FETCH}, so
              the count is a floor rather than a total.
            </p>
          ) : null}
        </div>

        {/*
        The selected dispatch, beside the queue. Nothing opens until a row is chosen: this board is
        ordered by deadline, so its first row is the most urgent rather than the one to read.
      */}
        {selected === undefined ? null : (
          <RecordDetail
            record={dispatchDetail(selected)}
            open
            onClose={() => setSelectedDemandId(undefined)}
          />
        )}
      </div>

      {assigning ? (
        <ManualAssign
          client={manual}
          tenantId={tenantId}
          demandId={selectedDemandId}
          onClose={() => {
            setAssigning(false);
          }}
          onSent={() => {
            // The row's state changes the moment the offer exists, and an operator who just sent
            // one is the person most entitled to see that it landed.
            void load();
          }}
        />
      ) : null}

      <div className="queue__note">
        <span aria-hidden="true" className="queue__note-glyph">
          info
        </span>
        <span>
          Offers go to one driver at a time with a countdown; the next driver is tried when one
          declines or times out.
        </span>
      </div>
    </section>
  );
}

/**
 * Export what the operator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page: an operator exporting a queue wants the queue,
 * not the ten rows that happened to be on screen. Excel takes an HTML table, which is what
 * `application/vnd.ms-excel` has always meant in practice, and PDF is the browser's own print
 * dialogue.
 */
function exportRows(rows: readonly DispatchOperation[], format: ExportFormat): void {
  const header = ["Request", "State"];
  const body = rows.map((row) => [row.demandId, readableStatus(row.status)]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "demand-queue.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "demand-queue.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>demand queue</title></head><body><h2 style="font-family:sans-serif">demand queue</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}

/**
 * Re-exported so the screen's own vocabulary is reachable from the screen.
 *
 * It lives in `demand-queue.ts` with the rest of the logic, where it can be exercised without
 * rendering anything.
 */
export { readableStatus };
